import * as React from 'react';
import { cx } from '../utils';
import { tintVar, type StyledProps, type TintName } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export interface LedgerRowProps extends StyledProps, UnsafeStyleProps {
  label: React.ReactNode;
  value: React.ReactNode;
  bold?: boolean;
  muted?: boolean;
  highlight?: boolean;
  indent?: boolean;
  divider?: boolean;
  /** Semantic tone for the value text. Resolves to
   *  `var(--color-{tone}-700)` via `tintVar` against the
   *  whitelisted `TintName` set. Audit #13 + #9 (2026-05-16): semantic
   *  axis re-introduced once the underlying TintName→v5-token mapping
   *  was fixed. Use this for status-coloured rows (savings, warnings). */
  tone?: TintName;
  /** ADR-017 §"unsafe_*" escape hatch — accepts any valid CSS colour
   *  expression. Audit #13: renamed from `color` to signal the bypass.
   *  Takes priority over `tone` when both are passed. */
  unsafe_color?: string;
}

export function LedgerRow({
  label,
  value,
  bold,
  muted,
  highlight,
  indent,
  divider,
  tone,
  unsafe_color,
  className,
  unsafe_style,
}: LedgerRowProps) {
  /* Resolution order: unsafe_color (free-form) > tintVar(tone)
     (semantic, var(--color-{tone}-700)) > inherit. tintVar gates
     against TINT_NAMES so a typo silently no-ops. */
  const valueColor: string | undefined = unsafe_color ?? tintVar(tone);
  return (
    <div
      className={cx(
        'j-ledger-row',
        bold && 'j-ledger-row-bold',
        muted && 'j-ledger-row-muted',
        highlight && 'j-ledger-row-highlight',
        indent && 'j-ledger-row-indent',
        divider && 'j-ledger-row-divider',
        className,
      )}
      style={unsafe_style}
    >
      <span className="j-ledger-row-label">{label}</span>
      <span className="j-ledger-row-value" style={valueColor ? { color: valueColor } : undefined}>
        {value}
      </span>
    </div>
  );
}
